import { useEffect, useState } from 'react'
import { Control, useFieldArray, UseFormSetValue } from 'react-hook-form'
import { Button, Dropdown, MultiSelectStoreDropdown } from 'components/common'
import { CloseIcon, PlusIcon } from 'components/icons'
import { fetchFtpProfiles } from 'utils/api/ftpProfileApi'

type Props = {
  control: Control<any>
  setValue: UseFormSetValue<any>
}

// Lets an aggregator override its default FTP config for specific groups of
// stores (e.g. Talabat Group A -> FTP Profile A), by picking a saved
// FtpProfile per group. Purely optional - stores left unmapped keep using the
// aggregator's own `ftp` config, so leaving this section empty changes nothing.
const FtpProfileOverrides = ({ control, setValue }: Props) => {
  const [profileOptions, setProfileOptions] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ftpProfileMappings',
  })

  const getFtpProfiles = async () => {
    try {
      const { data } = await fetchFtpProfiles({ limit: 500, page: 1 })
      const options = (data?.docs || []).map((profile: any) => ({
        label: profile.name,
        value: profile._id,
        disabled: false,
      }))
      setProfileOptions(options)
    } catch (error) {
      setProfileOptions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getFtpProfiles()
  }, [])

  return (
    <div>
      <p className="text-sm font-bold text-sidebar-primary">
        FTP Profile Overrides
      </p>
      <p className="text-xs text-gray-500">
        Optional. Route specific groups of stores to a different saved FTP
        profile instead of the default FTP configuration above. Manage profiles
        from the <strong>FTP Profiles</strong> page.
      </p>
      {/* Scheduled runs only split activated stores into profile batches */}
      <p className="mb-4 mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <strong>Note:</strong> A store must also be selected in{' '}
        <strong>Activated Stores</strong> (Schedule section) for its override to
        apply on the scheduled run. Add it there first, then map it to a profile
        here.
      </p>

      <div className="mb-3 flex items-center justify-end">
        <Button
          type="button"
          variant="custom"
          icon={<PlusIcon />}
          buttonText="Add Override"
          disabled={loading || profileOptions.length === 0}
          className="flex items-center gap-1.5 rounded-md border border-button-primary px-2.5 py-1.5 text-xs font-semibold text-button-primary transition-colors hover:bg-button-primary hover:text-white disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
          onClick={() => append({ ftpProfileId: '', stores: [] })}
        />
      </div>

      {fields.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 px-4 py-5 text-center">
          <p className="text-sm text-gray-400">
            No FTP profile overrides configured.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="flex flex-col gap-2 rounded-lg border border-gray-200 p-2.5 mid-md:flex-row mid-md:items-start"
          >
            <div className="w-full mid-md:w-64">
              <Dropdown
                required
                label="FTP Profile"
                defaultValue={(field as any).ftpProfileId}
                setValue={setValue}
                options={
                  profileOptions.length > 0
                    ? profileOptions
                    : [
                        {
                          label: 'No FTP Profiles Found',
                          value: '',
                          disabled: true,
                        },
                      ]
                }
                name={`ftpProfileMappings.${index}.ftpProfileId`}
              />
            </div>

            <div className="w-full mid-md:w-72">
              <MultiSelectStoreDropdown
                required
                resetForm
                label="Stores"
                name={`ftpProfileMappings.${index}.stores`}
                setValue={setValue}
                defaultSelected={(field as any).stores}
                isActive
              />
            </div>

            <div className="flex shrink-0 justify-end mid-md:pt-6">
              <button
                type="button"
                aria-label="Remove override"
                onClick={() => remove(index)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-colors hover:border-error/40 hover:bg-error/10 hover:text-error"
              >
                <span className="h-3 w-3">
                  <CloseIcon />
                </span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default FtpProfileOverrides
